import type { ThemePreset } from '../models';

export interface ThemePresetValues {
    fontFamily: string;
    bg: string;
    surface: string;
    text: string;
    muted: string;
    border: string;
    radius: string;
    radiusLg: string;
    sectionPadY: string;
    sectionPadX: string;
    sectionPadYTight: string;
    heroPadY: string;
    inputBg: string;
    accent: string;
    accentAlt: string;
    accentText: string;
    accentAltText: string;
    shadow: string;
}

export const THEME_PRESETS: Record<ThemePreset, ThemePresetValues> = {
    minimal: {
        fontFamily: '"Avenir Next", "Avenir", "Helvetica Neue", sans-serif',
        bg: '#ffffff',
        surface: '#f8fafc',
        text: '#1f2937',
        muted: '#6b7280',
        border: '#e5e7eb',
        radius: '8px',
        radiusLg: '12px',
        sectionPadY: '56px',
        sectionPadX: '28px',
        sectionPadYTight: '40px',
        heroPadY: '72px',
        inputBg: '#ffffff',
        accent: '#0f172a',
        accentAlt: '#1f2937',
        accentText: '#ffffff',
        accentAltText: '#ffffff',
        shadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
    },
    elegant: {
        fontFamily: '"Merriweather", "Georgia", serif',
        bg: '#ffffff',
        surface: '#f8fafc',
        text: '#0f172a',
        muted: '#475569',
        border: '#e2e8f0',
        radius: '12px',
        radiusLg: '16px',
        sectionPadY: '64px',
        sectionPadX: '32px',
        sectionPadYTight: '48px',
        heroPadY: '80px',
        inputBg: '#ffffff',
        accent: '#2563eb',
        accentAlt: '#4f46e5',
        accentText: '#ffffff',
        accentAltText: '#ffffff',
        shadow: '0 8px 20px rgba(15, 23, 42, 0.08)',
    },
    bold: {
        fontFamily: '"Space Grotesk", "Helvetica Neue", sans-serif',
        bg: '#f8fafc',
        surface: '#ffffff',
        text: '#0b1220',
        muted: '#475569',
        border: '#d1d5db',
        radius: '18px',
        radiusLg: '22px',
        sectionPadY: '72px',
        sectionPadX: '36px',
        sectionPadYTight: '56px',
        heroPadY: '92px',
        inputBg: '#ffffff',
        accent: '#0ea5e9',
        accentAlt: '#14b8a6',
        accentText: '#ffffff',
        accentAltText: '#ffffff',
        shadow: '0 12px 28px rgba(14, 165, 233, 0.18)',
    },
};

export function getThemePreset(themePreset?: ThemePreset): ThemePresetValues {
    return THEME_PRESETS[themePreset ?? 'elegant'];
}

export function getThemeVars(themePreset?: ThemePreset): Record<string, string> {
    const preset = getThemePreset(themePreset);
    return {
        '--theme-font': preset.fontFamily,
        '--theme-bg': preset.bg,
        '--theme-surface': preset.surface,
        '--theme-text': preset.text,
        '--theme-muted': preset.muted,
        '--theme-border': preset.border,
        '--theme-radius': preset.radius,
        '--theme-radius-lg': preset.radiusLg,
        '--theme-section-pad-y': preset.sectionPadY,
        '--theme-section-pad-x': preset.sectionPadX,
        '--theme-section-pad-y-tight': preset.sectionPadYTight,
        '--theme-hero-pad-y': preset.heroPadY,
        '--theme-input-bg': preset.inputBg,
        '--theme-accent': preset.accent,
        '--theme-accent-alt': preset.accentAlt,
        '--theme-accent-text': preset.accentText,
        '--theme-accent-alt-text': preset.accentAltText,
        '--theme-shadow': preset.shadow,
    };
}
